// @flow
import { getAuthToken, getBidderId } from '../modules/user';
import api from '../api/conversation';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { fetchConversationFoldersIfNeeded } from './conversationFolders';
import { getDeployment } from '../modules/config';
import type { GlobalState } from '../rootReducer';
import { LOG_OUT_BIDDER } from './actions/user';

/* Action Types */
export const LOAD_CONVERSATIONS_FAIL = 'LOAD_CONVERSATIONS_FAIL';
export const LOAD_CONVERSATIONS_REQUEST = 'LOAD_CONVERSATIONS_REQUEST';
export const LOAD_CONVERSATIONS_SUCCESS = 'LOAD_CONVERSATIONS_SUCCESS';

export const LOAD_CONVERSATION_FAIL = 'LOAD_CONVERSATION_FAIL';
export const LOAD_CONVERSATION_REQUEST = 'LOAD_CONVERSATION_REQUEST';
export const LOAD_CONVERSATION_SUCCESS = 'LOAD_CONVERSATION_SUCCESS';

export const CONVERSATIONS_ACTION_FAIL = 'la/domain/conversations/ACTION_FAIL';
export const CONVERSATIONS_ACTION_REQUEST = 'la/domain/conversations/ACTION_REQUEST';
export const CONVERSATIONS_ACTION_SUCCESS = 'la/domain/conversations/ACTION_SUCCESS';

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: 0,
    loading: false,
    pagination: {
        page: 1,
        pageSize: 48,
        totalPages: 1,
        totalResults: 0,
    },
    shownConversations: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let existingPagination;
    let shownConversations;

    switch (action.type) {
        case LOAD_CONVERSATIONS_FAIL:
        case LOAD_CONVERSATION_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_CONVERSATIONS_REQUEST:
        case LOAD_CONVERSATION_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_CONVERSATIONS_SUCCESS:
            existing = cloneDeep(state.byId);
            existingPagination = { ...state.pagination };
            shownConversations = [];

            if (action.payload.payload) {
                action.payload.payload.forEach(convo => {
                    existing[convo.conversationId] = { ...convo };
                    shownConversations.push(convo.conversationId);
                });
            }

            if (action.payload?.meta?.pagination) {
                const { pagination } = action.payload.meta;
                existingPagination = {
                    page: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                    totalPages: pagination.totalPages,
                    totalRecords: pagination.totalRecordCount,
                };
            }

            return {
                ...state,
                byId: existing,
                loaded: action.meta.actionTime,
                loading: false,
                pagination: existingPagination,
                shownConversations,
            };
        case LOAD_CONVERSATION_SUCCESS:
            existing = cloneDeep(state.byId);

            if (action.payload.payload) {
                action.payload.payload.forEach(convo => {
                    existing[convo.conversationId] = { ...convo };
                });
            }

            return {
                ...state,
                byId: existing,
                loaded: action.meta.actionTime,
                loading: false,
            };
        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.conversation;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

const conversationsSelector = createSelector(stateSelector, state => state.shownConversations);

export const getConversationPagination = createSelector(stateSelector, state => state.pagination);

export const getConversations = createSelector([conversationsSelector, byIdSelector], (conversations, byId) =>
    conversations.map(convo => byId[convo])
);

export const getConversation = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForConversation = loadedSelector;

export const getConversationLoading = loadingSelector;

const shouldFetchConversation = createSelector(
    [getConversation, getLoadTimeForConversation, getConversationLoading],
    (conversation, loaded, loading) => {
        return !loading;
    }
);

type ConversationQueryType = {|
    folderId: number,
    page?: number,
    pageSize?: number,
    search?: string,
    // sort: string,
|};

const loadConversations = (query: ConversationQueryType) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATIONS_REQUEST,
        });
        const response = await api.fetchConversations({ authToken, bidderId, deployment, query });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response,
            type: LOAD_CONVERSATIONS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CONVERSATIONS_FAIL,
        });
    }
};

const loadConversation = (conversationId: number) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATION_REQUEST,
        });
        const response = await api.fetchConversation({ authToken, bidderId, conversationId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response,
            type: LOAD_CONVERSATION_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CONVERSATION_FAIL,
        });
    }
};

export const fetchConversationsIfNeeded = (query: ConversationQueryType) => async (
    dispatch: Function,
    getState: Function
) => {
    if (true) {
        return dispatch(loadConversations(query));
    }
    return Promise.resolve();
};

export const fetchConversationIfNeeded = (conversationId: number) => async (dispatch: Function, getState: Function) => {
    if (shouldFetchConversation(getState(), conversationId)) {
        return dispatch(loadConversation(conversationId));
    }
    return Promise.resolve();
};

const actions = {
    archive: api.archiveConversation,
    markRead: api.markConversationRead,
    markUnread: api.markConversationUnread,
    move: api.moveConversation,
};

export const conversationAction = (
    conversations: any[],
    action: string,
    query: Object,
    markOnly: boolean = false,
    folderId?: number
) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: CONVERSATIONS_ACTION_REQUEST,
        });
        const response = await actions[action]({ authToken, bidderId, conversations, deployment, folderId });

        dispatch(fetchConversationFoldersIfNeeded());

        dispatch({
            meta: { action, actionTime: Date.now(), conversations },
            payload: response,
            type: CONVERSATIONS_ACTION_SUCCESS,
        });

        if (markOnly) {
            return Promise.resolve();
        }
        return dispatch(fetchConversationsIfNeeded(query));
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: CONVERSATIONS_ACTION_FAIL,
        });
    }
};
